<template>
  <aside class="portal-sidebar mt-4 w-full shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_16px_40px_-34px_rgba(15,23,42,.65)] md:sticky md:top-20 md:mt-6 md:w-[196px] md:self-start">
    <!-- Mobile toggle -->
    <div class="flex items-center justify-between border-b border-slate-100 px-3 py-2.5 md:hidden">
      <span class="inline-flex items-center gap-2 text-xs font-extrabold text-[#0b5557]"><span class="h-1.5 w-1.5 rounded-full bg-amber-400"></span>Portal Menüsü</span>
      <button type="button" :aria-expanded="mobileOpen" aria-label="Portal menüsünü aç veya kapat" class="flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100" @click="mobileOpen = !mobileOpen">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path v-if="!mobileOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
          <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Sidebar content -->
    <nav :class="['md:block', mobileOpen ? 'block' : 'hidden']" class="space-y-0.5 p-2">
      <!-- User info (if logged in) -->
      <div v-if="auth && auth.id" class="mb-2 rounded-lg border border-slate-200 bg-[#f7f9f7] p-2">
        <div class="flex items-center gap-2">
          <div class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#0b5557] text-[11px] font-black text-white shadow-sm">
            
            <img v-if="auth.U_Photo" :src="auth.U_Photo" alt="Profile" class="h-full w-full object-cover" />
            <span v-else>
              {{ (auth.U_rname || '').charAt(0) }}{{ (auth.U_surname || '').charAt(0) }}
            </span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="mb-0 truncate text-[11px] font-extrabold leading-4 text-slate-800">{{ auth.U_rname }} {{ auth.U_surname }}</p>
            <p class="mb-0 truncate text-[9px] leading-3 text-slate-500">{{ auth.U_Mobil || auth.email || '' }}</p>
          </div>
        </div> 
      </div>

      <!-- Menu items -->
      <template v-for="item in menuItems" :key="item.key">
        <!-- Divider -->
        <div v-if="item.divider" class="my-1.5 border-t border-slate-100"></div>
        <!-- Section label -->
        <p v-else-if="item.section" class="mb-0 px-2 pb-1 pt-1.5 text-[8px] font-extrabold uppercase tracking-[.16em] text-slate-400">{{ item.section }}</p>
        <!-- Regular menu item -->
        <a
          v-else-if="!item.hidden"
          @click.prevent="navigate(item)"
          :href="item.path"
          :class="[
            'group flex items-center gap-2 rounded-lg border px-2 py-1.5 text-[11px] leading-4 transition-all cursor-pointer',
            isActive(item.path)
              ? 'border-[#d3e3df] bg-[#eef5f3] font-extrabold text-[#0a5153] shadow-[inset_2px_0_0_#0b5557]'
              : 'border-transparent text-slate-600 hover:border-slate-100 hover:bg-slate-50 hover:text-slate-900'
          ]"
        >
          <span
            :class="[
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors',
              isActive(item.path)
                ? 'bg-white text-[#0b5557] shadow-sm'
                : 'bg-slate-100 text-slate-500 group-hover:bg-white group-hover:text-[#0b5557]'
            ]"
          >
            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getIconPath(item.key)" />
            </svg>
          </span>
          <span class="flex-1">{{ item.label }}</span>
          <span v-if="item.badge" class="ml-auto rounded px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide" :class="item.badgeClass || 'bg-amber-50 text-amber-700'">{{ item.badge }}</span>
          <span v-if="item.requiresAuth && (!auth || !auth.id)" class="ml-auto">
            <svg class="h-3 w-3 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
          </span>
        </a>
      </template>

      <!-- Login/Logout -->
      <div class="mt-1.5 border-t border-slate-100 pt-1.5">
        <a
          v-if="!auth || !auth.id"
          @click.prevent="navigate({ path: '/user/home/giris' })"
          href="/user/home/giris"
          :class="[
            'flex items-center gap-2 rounded-lg border px-2 py-1.5 text-[11px] transition-all cursor-pointer',
            isActive('/user/home/giris')
              ? 'border-[#d3e3df] bg-[#eef5f3] font-extrabold text-[#0a5153]'
              : 'border-transparent font-semibold text-[#0b5557] hover:bg-slate-50'
          ]"
        >
          <span class="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-[#0b5557]">
            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a5 5 0 10-9.607 2H3v4h4v-2h2v-2h1.393A4.978 4.978 0 0015 7z"/></svg>
          </span>
          <span>Giriş Yap / Üye Ol</span>
        </a>
        <template v-else>
          <a
            @click.prevent="navigate({ path: '/user/settings' })"
            href="/user/settings"
            class="group flex items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 text-[11px] text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 cursor-pointer"
          >
            <span class="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-500 group-hover:bg-white group-hover:text-[#0b5557]">
              <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.983 5.25c.603 0 1.142.39 1.331.966l.238.72a1.5 1.5 0 001.62 1.013l.758-.09a1.4 1.4 0 011.397.72c.29.503.252 1.131-.097 1.596l-.486.647a1.5 1.5 0 000 1.796l.486.647c.349.465.387 1.093.097 1.596a1.4 1.4 0 01-1.397.72l-.758-.09a1.5 1.5 0 00-1.62 1.013l-.238.72a1.4 1.4 0 01-1.331.966h-.766a1.4 1.4 0 01-1.331-.966l-.238-.72a1.5 1.5 0 00-1.62-1.013l-.758.09a1.4 1.4 0 01-1.397-.72 1.5 1.5 0 01.097-1.596l.486-.647a1.5 1.5 0 000-1.796l-.486-.647a1.5 1.5 0 01-.097-1.596 1.4 1.4 0 011.397-.72l.758.09a1.5 1.5 0 001.62-1.013l.238-.72a1.4 1.4 0 011.331-.966h.766z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15a3 3 0 100-6 3 3 0 000 6z"/></svg>
            </span>
            <span>Ayarlar</span>
          </a>
          <a
            @click.prevent="handleLogout"
            class="group flex items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 text-[11px] text-slate-500 transition hover:bg-rose-50 hover:text-rose-700 cursor-pointer"
          >
            <span class="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-500 group-hover:bg-white group-hover:text-rose-600">
              <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H9"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 20H6a2 2 0 01-2-2V6a2 2 0 012-2h7"/></svg>
            </span>
            <span>Çıkış Yap</span>
          </a>
        </template>
      </div>
    </nav>
  </aside>
</template>

<script setup>
import { ref, computed } from 'vue'

const { goPath, logout } = useCommon()
const auth = useUser()
const route = useRoute()
const mobileOpen = ref(false)

const menuItems = computed(() => {
  const loggedIn = auth.value && auth.value.id
  return [
    { key: 'home', path: '/user/home', label: loggedIn ? 'Kontrol Paneli' : 'Ana Sayfa' },
    { divider: true },
    { section: 'İşlemler' },
    { key: 'payment', path: '/user/payment', label: 'Ödeme Yap' },
    { key: 'doc-upload', path: '/user/doc-upload', label: 'Doküman Yükle' },
    { key: 'reservation', path: '/user/home/rezervasyon', label: 'Rezervasyon Görüntüle' },
    { divider: true },
    { section: 'Bilgi & Destek' },
    { key: 'show-ticket', path: '/user/show-ticket', label: 'Bilet & Vize', requiresAuth: true, badge: 'Beta', badgeClass: 'bg-amber-50 text-amber-700' },
    { key: 'faq', path: '/user/home/sss', label: 'S.S.S.' },
  ]
})

const iconPaths = {
  home: 'M3 10.5L12 3l9 7.5V21a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1v-10.5z',
  payment: 'M3 7h18v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7zm0 4h18M7 15h4',
  'doc-upload': 'M7 3h7l5 5v13a1 1 0 01-1 1H7a2 2 0 01-2-2V5a2 2 0 012-2zm7 1v5h5M12 17v-6m0 0l-2 2m2-2l2 2',
  reservation: 'M8 7V3m8 4V3M5 9h14M6 5h12a1 1 0 011 1v13a1 1 0 01-1 1H6a1 1 0 01-1-1V6a1 1 0 011-1z',
  'show-ticket': 'M4 8h16v8H4V8zm0 0l3 3h10l3-3M9 16v2m6-2v2',
  faq: 'M8.5 9a3.5 3.5 0 117 0c0 2-1.5 2.8-2.5 3.5-.9.6-1.5 1.1-1.5 2v.5M12 19h.01M21 12A9 9 0 113 12a9 9 0 0118 0z',
  dashboard: 'M4 5h7v7H4V5zm9 0h7v4h-7V5zM4 14h4v5H4v-5zm6 0h10v5H10v-5z'
}

const getIconPath = (key) => iconPaths[key] || iconPaths.home

const isActive = (path) => {
  if (!path) return false
  // exact match for home
  if (path === '/user/home') return route.path === '/user/home' || route.path === '/user/home/'
  return route.path.startsWith(path)
}

const navigate = (item) => {
  if (item.requiresAuth && (!auth.value || !auth.value.id)) {
    // redirect to login first
    goPath('/user/home/giris')
    mobileOpen.value = false
    return
  }
  goPath(item.path)
  mobileOpen.value = false
}

const handleLogout = () => {
  logout()
  goPath('/user/home')
}
</script>
